import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAgilePrices,
  fetchBatteryOperations,
  AgilePrice,
  BatteryOperation,
  ModbusBlob,
} from "@/lib/api";

type UseDashboardData = {
  prices: AgilePrice[];
  operations: BatteryOperation[];
  isLoading: boolean;
  error: unknown;
  lastUpdate: Date | null;
  valueCreated: number; // GBP
  latestOperation: BatteryOperation | null;
  currentPrice: AgilePrice | null;
  refetch: () => void;
};

const BASELINE_P_PER_KWH = 25;
const SLOT_HOURS = 0.5;
const MAX_ABS_KW = 20;

function clampKW(v: number | null | undefined): number | null {
  if (v == null || !isFinite(v as number)) return null;
  const x = Number(v);
  if (Math.abs(x) > MAX_ABS_KW) return Math.sign(x) * MAX_ABS_KW;
  return x;
}

function iso(dt: Date): string {
  return dt.toISOString();
}

function startOfTodayUTC(): Date {
  const now = new Date();
  now.setUTCHours(0, 0, 0, 0);
  return now;
}

/** Flatten Modbus blob into clean BatteryOperation */
function flattenBlob(b: ModbusBlob): BatteryOperation | null {
  if (!b || !b.timestamp) return null;
  const d = b.data ?? {};

  return {
    id: b.id,
    consus_id: b.consus_id,
    source_type: b.source_type,
    timestamp: b.timestamp,
    mode: b.mode ?? null,
    state_of_charge:
      typeof d.battery_soc === "number" ? d.battery_soc / 100 : null,
    battery_power_output:
            typeof d.total_load_power === "number" ? d.total_load_power / 1000 : null,

    grid_power:
      typeof d.meter_total_active_power === "number"
        ? Math.abs(d.meter_total_active_power / 1000)
        : null,
    load_power:
      typeof d.total_load_power === "number"
        ? Math.abs(d.total_load_power / 1000)
        : null,
    voltage:
      typeof d.battery_bms_voltage === "number" ? d.battery_bms_voltage : null,
    current:
      typeof d.battery_bms_current === "number" ? d.battery_bms_current : null,
    registers: d,
  };
}

function importKWFromMeter(op: BatteryOperation): number {
  const rawW =
    typeof op.registers?.meter_total_active_power === "number"
      ? op.registers.meter_total_active_power
      : typeof op.grid_power === "number"
      ? op.grid_power * 1000
      : null;
  if (rawW == null || !isFinite(rawW)) return 0;
  if (Math.abs(rawW) < 30) return 0;
  if (rawW < -30) return clampKW(Math.abs(rawW) / 1000) ?? 0;
  return 0;
}

export function useDashboardData(): UseDashboardData {
  const [prices, setPrices] = useState<AgilePrice[]>([]);
  const [operations, setOperations] = useState<BatteryOperation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const startAt = useMemo(() => startOfTodayUTC(), []);

  const refetch = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setIsLoading(true);
    setError(null);

    try {
      const now = new Date();
      const priceEnd = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const opsEnd = new Date(now.getTime() + 2 * 60 * 1000);

      const p = await fetchAgilePrices(
        `from=${encodeURIComponent(iso(startAt))}&to=${encodeURIComponent(iso(priceEnd))}`
      );
      setPrices(Array.isArray(p) ? p : []);

      const raw = await fetchBatteryOperations({
        start_time: startAt.toISOString(),
        end_time: iso(opsEnd),
        limit: 5000,
      });

      const mapped = (Array.isArray(raw) ? raw : [])
        .map(flattenBlob)
        .filter(Boolean) as BatteryOperation[];

      setOperations(mapped);
      setLastUpdate(new Date());
      console.log(`✅ Loaded ${mapped.length} ops, ${p?.length || 0} prices`);
    } catch (e) {
      setError(e);
      console.error("Fetch error", e);
    } finally {
      setIsLoading(false);
    }
  }, [startAt]);

  useEffect(() => {
    refetch();
    const iv = setInterval(refetch, 60_000);
    return () => {
      clearInterval(iv);
      abortRef.current?.abort();
    };
  }, [refetch]);

  const priceBySlot = useMemo(() => {
    const m = new Map<number, AgilePrice>();
    for (const pr of prices) {
      const t0 = new Date(pr.timestamp_start);
      m.set(Math.floor(t0.getTime() / 1.8e6) * 1.8e6, pr);
    }
    return m;
  }, [prices]);

  const { valueCreated, currentPrice, latestOperation } = useMemo(() => {
    let baseline = 0;
    let actual = 0;
    let latest: BatteryOperation | null = null;
    let cur: AgilePrice | null = null;
    const nowMs = Date.now();

    for (const op of operations) {
      const ts = new Date(op.timestamp);
      if (!isFinite(ts.getTime())) continue;
      if (!latest || new Date(latest.timestamp) < ts) latest = op;

      const key = Math.floor(ts.getTime() / 1.8e6) * 1.8e6;
      const price = priceBySlot.get(key);
      const import_kW = importKWFromMeter(op);
      const energy_kWh = import_kW * SLOT_HOURS;

      baseline += energy_kWh * (BASELINE_P_PER_KWH / 100);
      if (price?.import_price_p_per_kwh)
        actual += energy_kWh * (price.import_price_p_per_kwh / 100);

      if (!cur && price) {
        const s = new Date(price.timestamp_start).getTime();
        const e = new Date(price.timestamp_end).getTime();
        if (nowMs >= s && nowMs < e) cur = price;
      }
    }

    return {
      valueCreated: Math.max(0, baseline - actual),
      currentPrice: cur,
      latestOperation: latest,
    };
  }, [operations, priceBySlot]);

  return {
    prices,
    operations,
    isLoading,
    error,
    lastUpdate,
    valueCreated,
    latestOperation,
    currentPrice,
    refetch,
  };
}
